import { Formik } from "formik";
import pluralize from "pluralize";
import React, { useMemo } from "react";
import { Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import styled from "styled-components";
import * as yup from "yup";

import { FileRow } from "../../../common/components";
import {
    AmountField,
    FormFooter,
    SelectField,
    TextareaField,
} from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { CurrencyOptions } from "../../../constants/currencies";
import { HousingRuleOptions } from "../../../constants/housingRules";
import { PurposeCodeOptions, CNYPurposeCodeOptions } from "../../../constants/purposeCodes";
import {
    MinAmountLargerThanZero,
    SWIFTPattern,
    LinePattern,
    CharacterPattern,
} from "../../../misc/validators";
import { BankAccount, FormSubmissionHandler } from "../../../types/common";
import { InvoiceState } from "../../../types/invoice";
import { parseAmountString } from "../../../utils/amount";
import { isLtaProject } from "../../../utils/project";

import HousingRulesText from "./HousingRulesText";
import { withSwiftSupportedCharacters } from "../../../common/components/form/HOC/withSwiftSupportedCharacters";

export type PaymentFormValues = {
    bankAccount: string;
    currency: string;
    amount: string;
    purposeCode: string;
    remittanceInformation: string;
    housingRulesTypes: string[];
};



type PaymentFormProps = {
    projectName: string;
    invoice: InvoiceState;
    payerList: BankAccount[];
    values?: PaymentFormValues;
    onSubmit: FormSubmissionHandler<PaymentFormValues>;
};

const PaymentForm = ({
    projectName,
    invoice,
    payerList,
    values,
    onSubmit,
}: PaymentFormProps): JSX.Element => {
    const payerListOptions = useMemo<SelectOption[]>(
        () =>
            payerList.map((it) => ({
                value: `${it.bankAccountHolderName}-${it.bankAccountNumber}`,
                label: `${it.bankAccountHolderName}\n${it.bankAccountNumber}`,
            })),
        [payerList],
    );
    let housingRulesTypes = yup
        .array()
        .of(yup.string().required());
    if (!isLtaProject(projectName)) {
        housingRulesTypes = housingRulesTypes.min(1, "This field is required");
    }       
    const schema: yup.SchemaOf<PaymentFormValues> = yup.object().shape({
        bankAccount: yup.string().required(),
        currency: yup.string().required(),
        amount: yup.string().required().test(MinAmountLargerThanZero),
        purposeCode: yup.string().required(),
        remittanceInformation: yup
            .string()
            .required()
            .matches(
                SWIFTPattern,
                "Invalid character(s) input. Please ensure that the characters are alphanumeric and no other special characters, except for / - ? : ( ) . , ' + ",
            )
            .matches(
                LinePattern,
                "Exceed max number of lines (4)",
            )
            .matches(
                CharacterPattern,
                "Exceed max number of characters (35) in each line",
            )
            .test({
                name: "not-multi-line-empty",
                message: "This field is required",
                test: (value) =>
                    !!value &&
                    !!value
                        .split("\n")
                        .map((it) => it.trim())
                        .join(""),
            }),
        housingRulesTypes: housingRulesTypes,
    });
    return (
        <Formik
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSubmit}
            initialValues={
                values || {
                    bankAccount: "",
                    currency: parseAmountString(invoice.props.amount).currency,
                    amount: parseAmountString(invoice.props.amount).amount,
                    purposeCode: "",
                    remittanceInformation: "",
                    housingRulesTypes: [],
                }
            }
        >
            {({ values, handleSubmit }) => {
                const selectedBankAccount = payerList.find(
//                     (it) => it.bankAccountHolderName === values.bankAccount,
                    (it) => {
                        return (it.bankAccountHolderName + '-' + it.bankAccountNumber) === values.bankAccount
                    },
                );

                return (
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <Card.Body className="p-0 border-0">
                            <h1 className="mb-3 rem-1-5">
                                Payer Details
                            </h1>
                            <Row>
                                <Col>
                                    <SelectField
                                        name="bankAccount"
                                        label="Payer Bank Account"
                                        options={payerListOptions}
                                        autoFocus
                                        tall
                                        required={true}
                                    />
                                </Col>
                                <Col>
                                    {selectedBankAccount && (
                                        <>
                                            <p className="text-label-grey mb-2">
                                                Payer Bank
                                            </p>
                                            <p className="fw-bold mb-3">
                                                {
                                                    selectedBankAccount.swiftAddress
                                                }{" "}
                                                - {selectedBankAccount.bankName}
                                            </p>
                                        </>
                                    )}

                                </Col>
                            </Row>
                            <Row>
                                <Col></Col>
                                <Col>
                                    {selectedBankAccount && (
                                        <>
                                            <p className="text-label-grey mb-2">
                                                Payer Address
                                            </p>
                                            <p className="fw-bold mb-3">
                                                {
                                                    selectedBankAccount.registeredAddress
                                                }
                                            </p>
                                        </>
                                    )}
                                </Col>
                            </Row>
                            <hr className="bg-border-grey my-3" />
                            <h1 className="mb-3 rem-1-5">
                                Payment Details
                            </h1>
                            <Row>
                                <Col xs={6}>
                                    <InputGroup hasValidation>
                                        <SelectField
                                            name="currency"
                                            label="Currency"
                                            options={CurrencyOptions}
                                            disabled
                                            required={true}
                                        />
                                        <AmountField
                                            name="amount"
                                            label="Amount"
                                            disabled
                                            required={true}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <p className="text-label-grey mb-2">
                                        Payment Type
                                    </p>
                                    <p className="fw-bold mb-5">-</p>

                                    <SelectField
                                        name="purposeCode"
                                        label="Purpose Code"
                                        options={values.currency !== "CNY" ? PurposeCodeOptions : CNYPurposeCodeOptions}
                                        required={true}
                                    />

                                    <RemittanceInformationField
                                        defaultValue={values.remittanceInformation}
                                        className="mb-6"
                                        name="remittanceInformation"
                                        label="Remittance Information (35 characters x 4 lines)"
                                        maxLength={256}
                                        isAllowed={(formattedValue) => formattedValue.length < 256}
                                        required={true}
                                    />
                                </Col>
                                <Col>
                                    <p className="text-label-grey mb-2">
                                        Charge Bearer
                                    </p>
                                    <p className="fw-bold mb-5">SHAR</p>

                                    <SelectField
                                        name="housingRulesTypes"
                                        label="Housing Rules Types"
                                        options={HousingRuleOptions}
                                        isMulti
                                        required={!isLtaProject(projectName)}
                                        disabled={isLtaProject(projectName)}
                                    />
                                    {values.housingRulesTypes.length > 0 && (
                                        <>
                                            <p className="fs-8 text-label-grey mb-2">
                                                Housing Rules Description
                                            </p>
                                            <HousingRulesText
                                                className="fw-bold"
                                                rules={values.housingRulesTypes}
                                            />
                                        </>
                                    )}
                                </Col>
                            </Row>
                            <hr className="bg-border-grey my-3" />
                            <h1 className="mb-3 rem-1-5">
                                {pluralize(
                                    "Invoices",
                                    invoice.props.documents.length,
                                )}{" "}
                                to be Shared to Bank
                            </h1>
                            <p className="mb-5">
                                Please note that the following documents will be
                                shared to the bank over a separate email once
                                the payment order has been submitted.{" "}
                            </p>
                            {invoice.props.documents.map((file, index) => (
                                <FileRow key={index} file={file} />
                            ))}
                            <hr className="bg-border-grey my-3" />
                        </Card.Body>

                        <FormFooter className="p-0 py-0 text-end">
                            <Button variant="light.primary" type="submit">
                                Next
                            </Button>
                        </FormFooter>
                    </Form>
                );
            }}
        </Formik>
    );
};

const SwiftSupportedCharsTextareaField =
    withSwiftSupportedCharacters(TextareaField);

const RemittanceInformationField = styled(SwiftSupportedCharsTextareaField)`
    textarea {
        font-family: Univers;
    }
`;

export default PaymentForm;
